import { useState } from 'react';

interface ProcessDetail {
  tag: string;
  title: string;
  description: string;
  steps: { number: string; name: string; detail: string; icon: string; }[];
}

const processDetails: ProcessDetail[] = [
  {
    tag: '레이저 가공',
    title: '레이저 가공 공정',
    description: 'CAD 설계부터 정밀 가공까지 완벽한 레이저 커팅 프로세스',
    steps: [
      { 
        number: '01', 
        name: 'CAD 설계도면', 
        icon: 'ri-draft-line', 
        detail: '고객 요구사항을 바탕으로 AutoCAD, SolidWorks 등 전문 CAD 소프트웨어를 활용하여 정밀 설계도면을 작성합니다. 치수 공차, 재질, 표면처리 등 모든 사양을 도면에 반영합니다.' 
      },
      { 
        number: '02', 
        name: '레이저/NCT 프로그램', 
        icon: 'ri-code-s-slash-line', 
        detail: '설계도면을 기반으로 레이저 커팅 및 NCT 펀칭 가공을 위한 NC 프로그램을 작성합니다. 최적의 네스팅(Nesting)으로 재료 손실을 최소화합니다.' 
      },
      { 
        number: '03', 
        name: '레이저 가공', 
        icon: 'ri-scissors-line', 
        detail: '고출력 파이버 레이저 장비와 NCT 프레스를 이용하여 금속 판재를 정밀하게 절단 및 타공합니다. 최대 ±0.1mm의 가공 정밀도를 보장합니다.' 
      },
    ],
  },
  {
    tag: '절곡',
    title: '절곡 공정',
    description: '정밀한 각도 제어로 완벽한 절곡 가공',
    steps: [
      { 
        number: '04', 
        name: '절곡 · TAP 가공', 
        icon: 'ri-scissors-cut-line', 
        detail: '신뢰성 있는 Bystronic 프레스 브레이크 장비를 이용하여 도면에 따라 정확한 각도로 절곡 작업을 수행합니다. 동시에 TAP 가공으로 나사산을 형성하여 조립 편의성을 높입니다. 최대 3,000mm 길이의 판재 절곡이 가능하며, 다양한 금형을 보유하여 복잡한 형상도 정밀하게 가공합니다.' 
      },
    ],
  },
  {
    tag: '용접',
    title: '용접 공정',
    description: '다양한 용접 방식으로 강도 높은 접합 실현',
    steps: [
      { 
        number: '05', 
        name: '용접', 
        icon: 'ri-fire-line', 
        detail: 'MIG, TIG, CO₂, 레이저 용접 등 다양한 용접 방식을 적용하여 강도 높은 접합을 실현합니다. 숙련된 용접 기술자가 직접 작업하여 용접 품질과 외관을 동시에 확보합니다. 소재 특성과 두께에 따라 최적의 용접 방법을 선택하여 변형을 최소화하고 견고한 결합력을 제공합니다.' 
      },
    ],
  },
  {
    tag: '조립',
    title: '조립 공정',
    description: '완제품 형태로 납품 가능한 정밀 조립',
    steps: [
      { 
        number: '07', 
        name: '조립', 
        icon: 'ri-settings-3-line', 
        detail: '각 부품을 도면 사양에 맞게 정확히 조립합니다. 필요 시 볼트, 너트, 인서트 등 체결 부품을 포함하여 완제품 형태로 납품 가능합니다. 조립 후 기능 테스트를 통해 모든 부품이 정상 작동하는지 확인하며, 고객의 요구사항에 맞춘 맞춤형 조립 서비스를 제공합니다.' 
      },
    ],
  },
];

export default function HeroSection() {
  const [selectedProcess, setSelectedProcess] = useState<ProcessDetail | null>(null);

  return (
    <>
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/a0a88683bf0d23695cc826df9233d124.jpg"
            alt="레이저 가공 현장"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/55"></div>
        </div>

        <div className="relative h-full flex flex-col justify-center px-6 md:px-20">
          <div className="max-w-4xl">
            <p className="text-orange-400 text-xs md:text-sm font-semibold tracking-widest uppercase mb-3 md:mb-5">
              Precision Metal Fabrication
            </p>
            <h1 className="text-3xl md:text-6xl font-bold text-white leading-tight mb-4 md:mb-6">
              정밀한 설계부터<br />
              완벽한 조립까지<br />
              <span className="text-orange-400">올인원 판금가공</span>
            </h1>
            <p className="text-sm md:text-xl text-white/80 font-light mb-7 md:mb-12 leading-relaxed">
              CAD 설계 기반 통합 생산 시스템
            </p>
            <div className="flex flex-wrap gap-2 md:gap-3">
              {processDetails.map((process) => (
                <button
                  key={process.tag}
                  onClick={() => setSelectedProcess(process)}
                  className="px-3 md:px-4 py-1.5 bg-white/15 backdrop-blur-sm border border-white/30 text-white text-xs md:text-sm rounded-full font-medium hover:bg-white/25 hover:border-white/50 transition-all cursor-pointer whitespace-nowrap"
                >
                  {process.tag}
                </button>
              ))}
            </div>
          </div>

          <div className="absolute bottom-8 md:bottom-16 left-6 right-6 md:left-20 md:right-20 flex justify-end">
            <p className="text-white/80 text-xs md:text-sm font-medium tracking-wide">
              ✦ 대기업 협력 프로젝트 경험 보유
            </p>
          </div>
        </div>
      </section>

      {/* 공정 상세 모달 */}
      {selectedProcess && (
        <div
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setSelectedProcess(null)}
        >
          <div
            className="bg-white rounded-t-3xl md:rounded-3xl shadow-2xl w-full md:max-w-2xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
              <div>
                <p className="text-xs font-semibold text-orange-500 uppercase tracking-widest mb-1">공정 상세</p>
                <h3 className="text-lg md:text-xl font-bold text-gray-900">{selectedProcess.title}</h3>
                <p className="text-xs text-gray-500 mt-1">{selectedProcess.description}</p>
              </div>
              <button
                onClick={() => setSelectedProcess(null)}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer ml-4 shrink-0"
              >
                <i className="ri-close-line text-gray-600 text-lg" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-3">
              {selectedProcess.steps.map((step) => (
                <div key={step.number} className="flex gap-4 p-4 rounded-xl bg-gray-50 hover:bg-orange-50 transition-colors duration-200">
                  <div className="shrink-0 flex flex-col items-center gap-1">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-orange-500 text-white">
                      <i className={`${step.icon} text-base`} />
                    </div>
                    <span className="text-xs font-bold text-orange-400">{step.number}</span>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm mb-1.5">{step.name}</p>
                    <p className="text-xs md:text-sm text-gray-600 leading-relaxed">{step.detail}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 pb-6">
              <button
                onClick={() => setSelectedProcess(null)}
                className="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}